/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

// ============================================================================
// Types
// ============================================================================

export interface ErrorAnalysisFiltersState {
  selectedSeverity: string | null;
  selectedFileType: string | null;
  selectedObaCategory: string | null;
  selectedPeriod: string | null; // reporting_period_ym e.g. "202403"

  setSelectedSeverity: (severity: string | null) => void;
  setSelectedFileType: (fileType: string | null) => void;
  setSelectedObaCategory: (obaCategory: string | null) => void;
  setSelectedPeriod: (period: string | null) => void;
  clearFilters: () => void;
}

// ============================================================================
// Store
// ============================================================================

const initialState = {
  selectedSeverity: null as string | null,
  selectedFileType: null as string | null,
  selectedObaCategory: null as string | null,
  selectedPeriod: null as string | null,
};

export const useErrorAnalysisFiltersStore = create<ErrorAnalysisFiltersState>()((set) => ({
  ...initialState,

  setSelectedSeverity: (selectedSeverity) => {
    set({ selectedSeverity });
  },
  setSelectedFileType: (selectedFileType) => {
    set({ selectedFileType });
  },
  setSelectedObaCategory: (selectedObaCategory) => {
    set({ selectedObaCategory });
  },
  setSelectedPeriod: (selectedPeriod) => {
    set({ selectedPeriod });
  },
  clearFilters: () => {
    set(initialState);
  },
}));

// ============================================================================
// Fine-grained Selectors
// ============================================================================

export const useErrorSelectedSeverity = () =>
  useErrorAnalysisFiltersStore((s) => s.selectedSeverity);
export const useErrorSelectedFileType = () =>
  useErrorAnalysisFiltersStore((s) => s.selectedFileType);
export const useErrorSelectedObaCategory = () =>
  useErrorAnalysisFiltersStore((s) => s.selectedObaCategory);
export const useErrorSelectedPeriod = () => useErrorAnalysisFiltersStore((s) => s.selectedPeriod);

/** Use shallow comparison so the selector result is stable and does not cause infinite re-renders. */
export const useErrorAnalysisFilters = () =>
  useErrorAnalysisFiltersStore(
    useShallow((s) => ({
      selectedSeverity: s.selectedSeverity,
      selectedFileType: s.selectedFileType,
      selectedObaCategory: s.selectedObaCategory,
      selectedPeriod: s.selectedPeriod,
    }))
  );

export const useSetErrorSelectedSeverity = () =>
  useErrorAnalysisFiltersStore((s) => s.setSelectedSeverity);
export const useSetErrorSelectedFileType = () =>
  useErrorAnalysisFiltersStore((s) => s.setSelectedFileType);
export const useSetErrorSelectedObaCategory = () =>
  useErrorAnalysisFiltersStore((s) => s.setSelectedObaCategory);
export const useSetErrorSelectedPeriod = () =>
  useErrorAnalysisFiltersStore((s) => s.setSelectedPeriod);
export const useClearErrorAnalysisFilters = () =>
  useErrorAnalysisFiltersStore((s) => s.clearFilters);
